import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { planLabel } from "@/lib/plans";

/**
 * Where Stripe Checkout sends people back to:
 *   /billing/result?status=success   paid
 *   /billing/result?status=cancelled left checkout without paying
 */
export default function PaymentResult() {
  const [params] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const ok = params.get("status") === "success";

  // The Stripe webhook updates the plan; refresh so the new credits show.
  useEffect(() => { if (ok) refreshUser(); }, [ok]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex h-full items-center justify-center overflow-y-auto px-6 py-10">
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-8 text-center shadow-card" data-testid={ok ? "payment-success" : "payment-cancelled"}>
        <span className={`mx-auto grid h-14 w-14 place-items-center rounded-full ${ok ? "bg-success-soft text-success" : "bg-warning-soft text-warning"}`}>
          {ok ? <CheckCircle2 className="h-7 w-7" /> : <XCircle className="h-7 w-7" />}
        </span>
        <h1 className="mt-5 font-display text-[28px] leading-tight tracking-[-0.02em]">
          {ok ? "Payment received" : "Payment not completed"}
        </h1>
        <p className="mt-2 text-[15px] leading-6 text-ink-secondary">
          {ok
            ? `You're on ${planLabel(user?.plan)}. Your credits are ready to use.`
            : "Nothing was charged. You can pick a plan again whenever you're ready."}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {ok ? (
            <Button asChild><Link to="/research">Start finding leads</Link></Button>
          ) : (
            <>
              <Button variant="outline" asChild><Link to="/research">Back to Research</Link></Button>
              <Button asChild><Link to="/choose-plan">Choose a plan</Link></Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
